import { t } from "@/i18n-js/instance";
import type { CommunityMember } from "@/react/types";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { BillingInfoContextProps } from "../BillingInfoContextProps";
import { localeRoot } from "../constants";
import { BillingInfoModalFormWrapper } from "./BillingInfoModalFormWrapper";

export type BillingInfoModalProps = Omit<
  BillingInfoContextProps,
  "communityMemberBillingInfo"
>;

const BillingInfoModal = ({
  communityMember,
  onSuccess,
  asPrerequisite,
}: BillingInfoModalProps) => {
  const modal = useModal();

  const onModalClose = () => {
    void modal.hide();
    modal.remove();
  };
  const onModalSuccess = () => {
    void onSuccess();
    void modal.hide();
    modal.remove();
  };

  return (
    <Modal
      title={t([localeRoot, "title"])}
      isOpen={modal.visible}
      onClose={onModalClose}
    >
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title>{t([localeRoot, "title"])}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={onModalClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <BillingInfoModalFormWrapper
          asPrerequisite={asPrerequisite}
          communityMember={communityMember}
          onSuccess={onModalSuccess}
        />
      </Modal.Content>
    </Modal>
  );
};

const BillingInfoModalDrawer = ModalManager.create<BillingInfoModalProps>(
  props => <BillingInfoModal {...props} />,
);

export const useBillingInfoModal = () => useModal(BillingInfoModalDrawer);

export type UseWithBillingInfoModal = (
  drawerComponentKey: Parameters<typeof useModal>[0],
  resolveCommunityMember: (props: any) => CommunityMember | undefined,
) => ReturnType<typeof useModal>;

export const useWithBillingInfoModal: UseWithBillingInfoModal = (
  drawerComponentKey,
  resolveCommunityMember,
) => {
  /**
   * Members with active subscriptions that predates the tax collection feature will not have a
   * billing info. We need to prompt them to add one before they can change their plan, billing
   * cycle, or add/edit their payment method.
   */
  const modal = useModal(drawerComponentKey);

  const billingInfoModal = useBillingInfoModal();
  return {
    ...modal,
    show: (props: any) => {
      const communityMember = resolveCommunityMember(props);
      if (!communityMember?.policies?.requires_billing_info) {
        return modal.show(props);
      }
      return billingInfoModal.show({
        asPrerequisite: true,
        communityMember,
        onSuccess: async () => {
          await modal.show(props);
        },
      });
    },
  };
};
